import { useState, useEffect, useCallback } from 'react';
import { Cookie, ShieldCheck } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Switch } from './ui/switch';
import { Button } from './ui/button';

const STORAGE_KEY = 'dyssc_cookie_consent';
const VERSION = 1;
export const OPEN_PREFS_EVENT = 'dyssc:open-cookie-prefs';

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== VERSION) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function persist(consent) {
  const payload = { ...consent, essential: true, version: VERSION, ts: Date.now() };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (e) { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent('dyssc:cookie-consent', { detail: payload })); } catch (e) { /* ignore */ }
  return payload;
}

const ToggleRow = ({ icon: Icon, title, desc, checked, onChange, disabled, testid }) => (
  <div className="flex items-start justify-between gap-4 py-4 border-b border-zinc-100 last:border-0">
    <div className="flex gap-3">
      <Icon className="w-5 h-5 text-zinc-400 mt-0.5 shrink-0" />
      <div>
        <p className="text-sm font-semibold text-[#111214]">{title}</p>
        <p className="text-xs text-[#52525B] mt-1 leading-relaxed">{desc}</p>
      </div>
    </div>
    <Switch
      checked={checked}
      onCheckedChange={onChange}
      disabled={disabled}
      data-testid={testid}
      className="mt-0.5 data-[state=checked]:bg-[#E02128]"
    />
  </div>
);

export function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  // Load any previously saved preferences into the toggles.
  useEffect(() => {
    const existing = readConsent();
    if (existing) {
      setAnalytics(!!existing.analytics);
      setMarketing(!!existing.marketing);
    }
  }, []);

  // Allow opening the preferences from anywhere (e.g. footer "Cookie Preferences").
  useEffect(() => {
    const handler = () => {
      const existing = readConsent();
      if (existing) {
        setAnalytics(!!existing.analytics);
        setMarketing(!!existing.marketing);
      }
      setOpen(true);
    };
    window.addEventListener(OPEN_PREFS_EVENT, handler);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, handler);
  }, []);

  const acceptAll = useCallback(() => {
    persist({ analytics: true, marketing: true });
    setAnalytics(true); setMarketing(true);
    setOpen(false);
  }, []);

  const rejectNonEssential = useCallback(() => {
    persist({ analytics: false, marketing: false });
    setAnalytics(false); setMarketing(false);
    setOpen(false);
  }, []);

  const savePreferences = useCallback(() => {
    persist({ analytics, marketing });
    setOpen(false);
  }, [analytics, marketing]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white border-zinc-200 max-w-lg" data-testid="cookie-prefs-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight text-[#111214]">Cookie Preferences</DialogTitle>
          <DialogDescription className="text-[#52525B]">
            Choose which cookies we may use. Essential cookies are always on so the store works.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <ToggleRow
            icon={ShieldCheck}
            title="Essential"
            desc="Required for cart, checkout, security and core site functionality. Always active."
            checked
            disabled
            onChange={() => {}}
            testid="cookie-toggle-essential"
          />
          <ToggleRow
            icon={Cookie}
            title="Analytics"
            desc="Help us understand how the site is used so we can improve it. No personal selling."
            checked={analytics}
            onChange={setAnalytics}
            testid="cookie-toggle-analytics"
          />
          <ToggleRow
            icon={Cookie}
            title="Marketing"
            desc="Used to show you relevant drops, restocks and offers. You can turn this off anytime."
            checked={marketing}
            onChange={setMarketing}
            testid="cookie-toggle-marketing"
          />
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
          <Button
            variant="outline"
            onClick={rejectNonEssential}
            className="border-zinc-300 text-[#111214] hover:bg-zinc-50 rounded-full w-full sm:w-auto"
            data-testid="cookie-modal-reject-btn"
          >
            Reject non-essential
          </Button>
          <Button
            onClick={savePreferences}
            className="bg-[#111214] hover:bg-black text-white rounded-full w-full sm:w-auto"
            data-testid="cookie-save-btn"
          >
            Save preferences
          </Button>
          <Button
            onClick={acceptAll}
            className="bg-[#E02128] hover:bg-[#c01c22] text-white rounded-full w-full sm:w-auto"
            data-testid="cookie-modal-accept-all-btn"
          >
            Accept all
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Helper for triggers elsewhere (e.g. footer link).
export function openCookiePreferences() {
  try { window.dispatchEvent(new CustomEvent(OPEN_PREFS_EVENT)); } catch (e) { /* ignore */ }
}
